// First-touch attribution storage. Captures the FIRST landing's UTM
// parameters (and click IDs) into localStorage with a 30-day TTL. Subsequent
// visits within the window do NOT overwrite — they keep the original "where
// did this lead come from" answer even if the user later returns via direct
// or organic. After 30 days the entry expires and the next URL-borne UTM
// becomes the new first-touch.

const TTL_MS = 30 * 24 * 60 * 60 * 1000;
const KEY = 'gd_attribution';
const PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'fbclid',
  'gclid',
  'yclid',
] as const;

const MAX_VALUE_LENGTH = 200;

export type Attribution = {
  ts: number;
  landing: string;
  referrer: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  fbclid?: string;
  gclid?: string;
  yclid?: string;
};

function isFresh(stored: unknown): stored is Attribution {
  if (!stored || typeof stored !== 'object') return false;
  const ts = (stored as { ts?: unknown }).ts;
  return typeof ts === 'number' && Date.now() - ts < TTL_MS;
}

// Plugin entry — captures UTM from the current URL's query string. Called
// once on client-side boot from plugins/utm-capture.client.ts. No-ops when
// the URL has no tracking params or when a fresh first-touch already exists.
export function captureAttributionFromUrl(): void {
  if (!import.meta.client) return;

  const search = new URLSearchParams(window.location.search);
  const incoming: Record<string, string> = {};
  for (const p of PARAMS) {
    const v = search.get(p);
    if (v && v.length > 0 && v.length <= MAX_VALUE_LENGTH) incoming[p] = v;
  }
  if (Object.keys(incoming).length === 0) return;

  try {
    const raw = window.localStorage.getItem(KEY);
    const existing = raw ? JSON.parse(raw) : null;
    if (isFresh(existing)) return; // first-touch wins
  } catch {
    // Corrupted JSON — fall through and overwrite below.
  }

  const payload: Attribution = {
    ts: Date.now(),
    landing: window.location.pathname,
    referrer: (document.referrer || '').slice(0, MAX_VALUE_LENGTH),
    ...incoming,
  };
  try {
    window.localStorage.setItem(KEY, JSON.stringify(payload));
  } catch {
    // Storage full / Safari private mode — non-fatal.
  }

  // Meta `_fbc` cookie format: `fb.1.{epoch_ms}.{fbclid}`. Used by future
  // Conversions API events to dedupe browser+server signals automatically.
  if (incoming.fbclid) {
    const maxAge = Math.floor(TTL_MS / 1000);
    document.cookie = `_fbc=fb.1.${Date.now()}.${incoming.fbclid}; path=/; max-age=${maxAge}; SameSite=Lax`;
  }
}

export function readAttribution(): Attribution | null {
  if (!import.meta.client) return null;
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return isFresh(parsed) ? (parsed as Attribution) : null;
  } catch {
    return null;
  }
}

// Meta CAPI Test Events — when the URL carries `?fbe_test_event_code=TESTxxx`
// we stash it in sessionStorage so subsequent ContactForm/BookingPanel
// submits attach `metaTestEventCode` to the payload, which the server then
// forwards to Graph API as `test_event_code`. With the same code present in
// browser Pixel and server CAPI, both events surface in Events Manager →
// Test Events tab and Meta auto-marks them as `Deduplicated`.
//
// Auto-evicts on tab close (sessionStorage). To clear within a session run
// `sessionStorage.removeItem('gd_meta_test_code')` in DevTools.
const TEST_CODE_KEY = 'gd_meta_test_code';
const TEST_CODE_RE = /^TEST[A-Z0-9]{1,28}$/;

export function captureMetaTestCodeFromUrl(): void {
  if (!import.meta.client) return;
  try {
    const code = new URLSearchParams(window.location.search).get('fbe_test_event_code');
    if (!code || !TEST_CODE_RE.test(code)) return;
    window.sessionStorage.setItem(TEST_CODE_KEY, code);
  } catch {
    // sessionStorage disabled (Safari private mode quirks) — non-fatal.
  }
}

export function readMetaTestCode(): string | null {
  if (!import.meta.client) return null;
  try {
    const code = window.sessionStorage.getItem(TEST_CODE_KEY);
    return code && TEST_CODE_RE.test(code) ? code : null;
  } catch {
    return null;
  }
}

// Reads the GA4 `client_id` for the current browser via gtag's async `get`
// command, so the server can later send a GA4 Measurement Protocol `purchase`
// tied to this session (→ Google Ads conversion import). Best-effort: resolves
// null if gtag isn't ready or doesn't answer within the timeout — it must
// NEVER block or fail a form submit.
export function readGaClientId(
  measurementId: string,
  timeoutMs = 800,
): Promise<string | null> {
  if (!import.meta.client || !measurementId) return Promise.resolve(null);
  const gtag = (window as unknown as { gtag?: (...args: unknown[]) => void }).gtag;
  if (typeof gtag !== 'function') return Promise.resolve(null);
  return new Promise((resolve) => {
    let settled = false;
    const finish = (v: string | null) => {
      if (settled) return;
      settled = true;
      resolve(v);
    };
    const timer = setTimeout(() => finish(null), timeoutMs);
    try {
      gtag('get', measurementId, 'client_id', (id: unknown) => {
        clearTimeout(timer);
        finish(typeof id === 'string' && id.length > 0 ? id : null);
      });
    } catch {
      clearTimeout(timer);
      finish(null);
    }
  });
}

export function useAttribution() {
  return { readAttribution, readGaClientId };
}
